import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { useNavigate, Link } from 'react-router-dom';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { useFacilityStore } from '../../../../app/z-store/store';
import Layout from '../../component/layout';
import { getPurchaseRequestsUrl } from "../../../../utils/urls";
import { getItem } from "../../../../utils/localStorage";

const PurchaseRequests = () => {
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    // State variables
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const [purchaseRequests, setPurchaseRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [currentUserId, setCurrentUserId] = useState(null);
    const [userRole, setUserRole] = useState('');
    const [userName, setUserName] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    
    // New state for comments dialog
    const [commentsDialogVisible, setCommentsDialogVisible] = useState(false);
    const [comments, setComments] = useState('');
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'

    // Status options
    const statusOptions = [
        { label: 'ALL', value: 'ALL' },
        { label: 'PENDING', value: 'PENDING' },
        { label: 'APPROVED', value: 'APPROVED' },
        { label: 'NOT APPROVED', value: 'NOT APPROVED' },
        { label: 'CANCELLED', value: 'CANCELLED' }
    ];

    // Get current user when component mounts
    useEffect(() => {
        const getCurrentUser = async () => {
            try {
                const currentUserValue = await getItem("APPUSER");
                
                let parsedUser;
                
                // Check if the value needs to be parsed
                if (typeof currentUserValue === "string") {
                    try {
                        parsedUser = JSON.parse(currentUserValue);
                    } catch (err) {
                        console.error("Error parsing APPUSER:", err);
                    }
                } else {
                    parsedUser = currentUserValue; // Assume it's already an object
                }
                
                // Safely access and set user details
                if (parsedUser?.user) {
                    setCurrentUserId(parsedUser.user.userId);
                    setUserRole(parsedUser.user.role);
                    setUserName(parsedUser.user.fullName);
                    setCurrentUser(parsedUser.user); // Store the entire user object
                } else {
                    console.error("Invalid user structure:", parsedUser);
                }
            } catch (err) {
                console.log("Error getting user:", err.message);
            }
        };
        
        getCurrentUser();
    }, []);

    // Fetch data when facility changes
    useEffect(() => {
        if (selectedFacilityId) {
            fetchPurchaseRequests();
        }
    }, [selectedFacilityId]);

    // Fetch purchase requests from the API
    const fetchPurchaseRequests = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${getPurchaseRequestsUrl}/${selectedFacilityId}`,
                'GET'
            );
            if (response.success) {
                setPurchaseRequests(response.data?.data || []);
            } else {
                console.error("Failed to fetch purchase requests:", response.error);
                setPurchaseRequests([]);
            }
        } catch (error) {
            console.error("Error fetching purchase requests:", error);
            setPurchaseRequests([]);
        } finally {
            setLoading(false);
        }
    };

    // Check if current user is an approver for a specific request
    const isUserApprover = (request) => {
        if (!currentUser?.userId || !Array.isArray(request.approvals)) return false;
      
        // find the approvals object for the current step
        const step = request.approvals.find(s => s.stepNumber === request.currentStep);
        if (!step || !Array.isArray(step.approvers)) return false;
      
        return step.approvers.some(approver =>
          String(approver.userId) === currentUser.userId &&
          approver.status === 'pending'
        );
    };

    // Check if request has been approved by the current user
    const hasUserApproved = (request) => {
        if (!currentUser?.userId || !request.approvals) return false;
        
        // Check if user has already approved in any step
        for (const step of request.approvals) {
            if (!Array.isArray(step.approvers)) continue;
            
            const hasApproved = step.approvers.some(approver => 
                String(approver.userId) === currentUser.userId && 
                approver.status === 'approved'
            );
            
            if (hasApproved) return true;
        }
        
        return false;
    };

    // Handle status filter changes
    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
    };

    // Filter logic for search + status
    const filteredRequests = Array.isArray(purchaseRequests)
        ? purchaseRequests.filter((item) => {
            const irfNumber = (item.irfNumber || '').toLowerCase();
            const department = (item.department || '').toLowerCase();
            const fromField = (item.from || '').toLowerCase();

            const textFilterPassed =
                irfNumber.includes(searchTerm.toLowerCase()) ||
                department.includes(searchTerm.toLowerCase()) ||
                fromField.includes(searchTerm.toLowerCase());

            const statusFilterPassed =
                statusFilter === 'ALL' || (item.status || '').toUpperCase() === statusFilter;

            return textFilterPassed && statusFilterPassed;
        })
        : [];

    // Styled status template
    const formatStatusBadge = (rowData) => {
        const statusStyles = {
            approved: { color: '#28a745', backgroundColor: '#e9fbe9' },
            pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
            'not approved': { color: '#dc3545', backgroundColor: '#fde9e9' },
            rejected: { color: '#dc3545', backgroundColor: '#fde9e9' },
            cancelled: { color: '#6c757d', backgroundColor: '#f1f1f1' }
        };
        
        const s = (rowData.status || '').toLowerCase();
        const style = statusStyles[s] || { color: '#6c757d', backgroundColor: '#f1f1f1' };
        
        return (
            <span
                style={{
                    ...style,
                    padding: '0.5em 1em',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'inline-block',
                }}
            >
                {rowData.status}
            </span>
        );
    };

    // Styled POStatus template
    const formatPOStatus = (rowData) => {
        const statusStyles = {
            approved: { color: '#28a745', backgroundColor: '#e9fbe9' },
            pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
            'not approved': { color: '#dc3545', backgroundColor: '#fde9e9' },
            'po raised': { color: '#28a745', backgroundColor: '#e9fbe9' },
            cancelled: { color: '#6c757d', backgroundColor: '#f1f1f1' }
        };
        
        const s = (rowData.poStatus || '').toLowerCase();
        const style = statusStyles[s] || { color: '#6c757d', backgroundColor: '#f1f1f1' };
        
        return (
            <span
                style={{
                    ...style,
                    padding: '0.5em 1em',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'inline-block',
                }}
            >
                {rowData.poStatus}
            </span>
        );
    };

    // Navigation handlers
    const handleViewDetails = (id) => {
        navigate(`/facility/procurement_management/purchase_requests/view/${id}`);
    };

    const handleEditRequest = (id) => {
        navigate(`/facility/procurement_management/purchase_requests/edit/${id}`);
    };

    // Open comments dialog for approval/rejection
    const openCommentsDialog = (id, action) => {
        setSelectedRequestId(id);
        setApprovalAction(action);
        setComments('');
        setCommentsDialogVisible(true);
    };

    // Submit approval/rejection with comments
    const submitApprovalAction = async () => {
        if (!selectedRequestId || !approvalAction) return;
        
        try {
            setLoading(true);
            setCommentsDialogVisible(false);
            
            // Determine endpoint and parameters based on action
            const endpoint = `/api/app/purchase-requests/approve/${selectedFacilityId}/${selectedRequestId}`;
            const approve = approvalAction === 'approve';
            
            const response = await makeRequest2(
                endpoint,
                'PUT',
                { 
                    approve,
                    comments,
                    userId: currentUser.userId 
                }
            );
            
            if (response.success) {
                toastify(
                    response.message || 
                    `Purchase request ${approve ? 'approved' : 'rejected'} successfully`, 
                    'success'
                );
                fetchPurchaseRequests();
            } else {
                toastify(
                    response.error || 
                    `Failed to ${approve ? 'approve' : 'reject'} purchase request`, 
                    'error'
                );
            }
        } catch (error) {
            console.error("Action error:", error);
            toastify(`Error ${approvalAction === 'approve' ? 'approving' : 'rejecting'} purchase request`, 'error');
        } finally {
            setLoading(false);
            setSelectedRequestId(null);
            setApprovalAction(null);
        }
    };

    // Action template
    const actionTemplate = (rowData) => {
        const isApprover = isUserApprover(rowData);
        const hasApproved = hasUserApproved(rowData);
        const isPending = (rowData.approvalStatus || '').toLowerCase() === 'pending' || 
                          (rowData.approvalStatus || '').toLowerCase() === 'in_progress';
        
        // If user has already approved, show only view button
        if (hasApproved) {
            return (
                <div className="d-flex gap-2">
                    <Button
                        icon="ti ti-eye"
                        className="p-button-rounded p-button-info"
                        onClick={() => handleViewDetails(rowData._id)}
                        tooltip="View"
                    />
                </div>
            );
        }
        
        // If user is an approver and request is in pending approval state
        if (isApprover && isPending) {
            return (
                <div className="d-flex gap-2">
                    <Button
                        icon="ti ti-eye"
                        className="p-button-rounded p-button-info"
                        onClick={() => handleViewDetails(rowData._id)}
                        tooltip="View"
                    />
                    <Button
                        icon="ti ti-check"
                        className="p-button-rounded p-button-success"
                        onClick={() => openCommentsDialog(rowData._id, 'approve')}
                        tooltip="Approve"
                    />
                    <Button
                        icon="ti ti-x"
                        className="p-button-rounded p-button-danger"
                        onClick={() => openCommentsDialog(rowData._id, 'reject')}
                        tooltip="Reject"
                    />
                </div>
            );
        }
        
        // For regular users or when request is not pending
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-info"
                    onClick={() => handleViewDetails(rowData._id)}
                    tooltip="View"
                />
                <Button
                    icon="ti ti-edit"
                    className="p-button-rounded p-button-warning"
                    onClick={() => handleEditRequest(rowData._id)}
                    tooltip="Edit"
                    disabled={(rowData.status || '').toLowerCase() !== 'pending'}
                />
            </div>
        );
    };

    // Dialog footer for comments
    const commentsDialogFooter = (
        <div>
            <Button 
                label="Cancel" 
                icon="pi pi-times" 
                className="p-button-text" 
                onClick={() => setCommentsDialogVisible(false)} 
            />
            <Button 
                label={approvalAction === 'approve' ? 'Approve' : 'Reject'} 
                icon={approvalAction === 'approve' ? 'pi pi-check' : 'pi pi-times'} 
                className={approvalAction === 'approve' ? 'p-button-success' : 'p-button-danger'} 
                onClick={submitApprovalAction} 
            />
        </div>
    );

    // If no facility is selected
    if (!selectedFacilityId) {
        return (
            <Layout>
                <div className="card">
                    <div className="card-body text-center">
                        Please select a facility to view purchase requests.
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Page Header with Breadcrumbs */}
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="">Purchase Requests</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <h5>All Purchase Requests</h5>
                        </div>
                        <ConfirmDialog />
                        <div className="card-body">
                            {/* Search and Add Button */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <input
                                        className="form-control"
                                        placeholder="Search IRF, Department, Creator..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-8 text-end">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() =>
                                            navigate('/facility/procurement_management/purchase_requests/add')
                                        }
                                    >
                                        Add Purchase Request
                                    </button>
                                </div>
                            </div>

                            {/* Status Filter Buttons */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="d-flex flex-wrap gap-2">
                                        {statusOptions.map(option => (
                                            <button
                                                key={option.value}
                                                className={`btn ${statusFilter === option.value ? 'btn-primary' : 'btn-outline-secondary'} text-uppercase`}
                                                onClick={() => handleStatusFilterChange(option.value)}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Table */}
                            <div className="table-responsive">
                                <DataTable
                                    value={filteredRequests}
                                    emptyMessage="No purchase requests found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    loading={loading}
                                    tableStyle={{ minWidth: '50rem' }}
                                    stateStorage="session"
                                    stateKey="purchase-request-state"
                                >
                                    <Column field="date" header="DATE" sortable body={(rowData) => {
                                        return new Date(rowData.date).toLocaleDateString();
                                    }} />
                                    <Column field="irfNumber" header="IRF NUMBER" sortable />
                                    <Column field="from" header="FROM" sortable />
                                    <Column field="department" header="DEPARTMENT" sortable />
                                    <Column field="status" header="STATUS" body={formatStatusBadge} sortable />
                                    <Column field="poStatus" header="PO STATUS" body={formatPOStatus} sortable />
                                    <Column
                                        header="OPTIONS"
                                        body={actionTemplate}
                                        style={{ width: '12rem' }}
                                    />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Comments Dialog for Approval/Rejection */}
            <Dialog
                header={`${approvalAction === 'approve' ? 'Approve' : 'Reject'} Purchase Request`}
                visible={commentsDialogVisible}
                style={{ width: '500px' }}
                footer={commentsDialogFooter}
                onHide={() => setCommentsDialogVisible(false)}
            >
                <div className="field">
                    <label htmlFor="comments">Comments (Optional)</label>
                    <InputTextarea
                        id="comments"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        rows={5}
                        cols={30}
                        className="w-100"
                        placeholder="Enter any comments regarding your decision..."
                    />
                </div>
            </Dialog>
        </Layout>
    );
};

export default PurchaseRequests;